import type { ModuleAccessLevel } from '../config/moduleAccessPolicy';
import { moduleAccessLevelShortLabel } from '../config/moduleAccessPolicy';

const ROLE_DISPLAY_NAMES: Record<string, string> = {
  owner: 'School administrator',
  teacher: 'Teacher',
  teacher_portal: 'Teacher',
  student_portal: 'Student',
  parent_portal: 'Parent / guardian',
  office_staff: 'Office staff',
  gatekeeper: 'Gatekeeper',
  'student portal user': 'Student / guardian portal',
};

export function friendlyRoleName(rawName: string): string {
  const key = rawName.trim().toLowerCase();
  if (ROLE_DISPLAY_NAMES[key]) return ROLE_DISPLAY_NAMES[key];
  return rawName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatPresetModuleAccess(
  entries: Array<{ module_code: string; access_level: ModuleAccessLevel; display_name?: string }>
): string {
  return entries
    .map((entry) => {
      const name = entry.display_name ?? friendlyModuleName(entry.module_code);
      const level = moduleAccessLevelShortLabel(entry.access_level);
      return `${name} (${level})`;
    })
    .join(' · ');
}

export function friendlyModuleName(moduleCode: string): string {
  const overrides: Record<string, string> = {
    class_attendance: 'Attendance',
    staff_leave: 'Leave',
    teacher_portal: 'Teacher Portal',
    report_card: 'Report Cards',
    pickup_pass: 'Issue pickup PIN / QR',
    fees: 'Collect & manage fees',
    fee: 'Collect & manage fees',
  };
  if (overrides[moduleCode]) return overrides[moduleCode];
  return moduleCode
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const FEE_SUBMODULE_LABELS: Record<string, string> = {
  collect: 'Collect payment (counter)',
  collect_payment: 'Collect payment (counter)',
  collection: 'Collect payment (counter)',
  fee_payments: 'Collect payment (counter)',
  payments: 'Collect payment (counter)',
  online: 'Pay online (Paytm)',
  online_payment: 'Pay online (Paytm)',
  pay_online: 'Pay online (Paytm)',
  razorpay: 'Pay online (Paytm)',
  paytm: 'Pay online (Paytm)',
  setup: 'Fee setup',
  fee_setup: 'Fee setup',
  invoices: 'Invoices & billing',
  fee_invoices: 'Invoices & billing',
  generate: 'Invoices & billing',
  advance: 'Advance / credit',
  credit: 'Advance / credit',
  family_advance: 'Advance / credit',
};

export function friendlySubmoduleName(moduleCode: string, submoduleId: string): string {
  const normalizedModule = moduleCode.trim().toLowerCase();
  const normalizedSub = submoduleId.trim().toLowerCase();
  if (normalizedModule === 'fees' || normalizedModule === 'fee') {
    if (FEE_SUBMODULE_LABELS[normalizedSub]) return FEE_SUBMODULE_LABELS[normalizedSub];
  }
  return submoduleId.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export const ACCESS_SETUP_STEPS = [
  {
    step: 1,
    title: 'Create a staff group',
    detail: 'A group matches a job: Teacher, Cashier, Receptionist, Gatekeeper. Start from a template or make your own.',
  },
  {
    step: 2,
    title: 'Choose what the group can do',
    detail: 'Expand each module and set Hidden, View only, or View & edit per sub-feature (e.g. exam marks vs report card templates).',
  },
  {
    step: 3,
    title: 'Add people to the group',
    detail: 'Create their login (or pick an existing one) and put them in the group. They must sign out and back in to see changes.',
  },
] as const;
